import type { InferRouterOutputs } from "@orpc/server";
import type { ApiRouter } from "@repo/api/orpc/router";
import { z } from "zod";

type Outputs = InferRouterOutputs<ApiRouter>["customers"];

export type CustomerList = Outputs["list"];
export type CustomerRow = CustomerList["customers"][number];
export type CustomerDetail = Outputs["get"];
export type CustomerAddress = CustomerDetail["addresses"][number];
export type MarketingState = CustomerRow["marketing"];

export const customersSearchSchema = z.object({
	q: z.string().optional(),
	marketing: z.enum(["opted_in", "opted_out", "unknown"]).optional(),
	store: z.string().optional(),
});
export type CustomersSearch = z.infer<typeof customersSearchSchema>;

export function listInputFrom(organizationId: string, search: CustomersSearch) {
	return {
		organizationId,
		...(search.q ? { q: search.q } : {}),
		...(search.marketing ? { marketing: search.marketing } : {}),
		...(search.store ? { storeId: search.store } : {}),
	};
}

export const MARKETING_META: Record<
	MarketingState,
	{ label: string; variant: "success" | "secondary" | "outline" }
> = {
	opted_in: { label: "Opted in", variant: "success" },
	opted_out: { label: "Opted out", variant: "secondary" },
	unknown: { label: "Not asked", variant: "outline" },
};

export const MARKETING_SOURCE_LABEL: Record<string, string> = {
	user_preferences: "WhatsApp settings",
	agent: "told the agent",
	status_131050: "WhatsApp stopped marketing",
	admin: "your team",
	checkout: "at checkout",
};

export const GEO_SOURCE_LABEL: Record<string, string> = {
	pin: "Pin",
	maps_link: "Maps link",
	area_text: "Typed area",
	retailer: "From your system",
	staff: "Set by your team",
};

export const customerName = (
	customer: Pick<CustomerRow, "name" | "profileName">,
) => customer.name ?? customer.profileName ?? "Unnamed shopper";

export function addressLine(address: CustomerAddress): string {
	return [
		address.area,
		address.building,
		address.floor ? `floor ${address.floor}` : null,
		address.apartment ? `apt ${address.apartment}` : null,
	]
		.filter(Boolean)
		.join(", ");
}
